import React, { FC, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useLotCalibrationData } from '../../../hooks/use-lot-calibration-data';
import OvErrorMarginControl from '../molecules/OvErrorMarginControl';
import OvHormoneCalibrationCard from './OvHormoneCalibrationCard';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import Alert from '@mui/material/Alert';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvLotCalibrationSectionProps {
  lotId: string;
  lhUnit?: string;
  pgUnit?: string;
  lrE3gUnit?: string;
}

// Constants
const ERROR_MARGIN_DEBOUNCE_MS = 300;
const DEFAULT_ERROR_MARGIN = 5;
const DEFAULT_LH_UNIT = 'mIU/mL';
const DEFAULT_PG_UNIT = 'ng/mL';
const DEFAULT_LR_E3G_UNIT = 'ng/mL';

/**
 * Validates and returns a safe unit value
 * @param unit - The unit value to validate
 * @param defaultUnit - The default unit to use if validation fails
 * @returns A valid unit string
 */
const getSafeUnit = (unit?: string, defaultUnit: string = ''): string => {
  if (!unit || unit === 'null' || unit === 'undefined' || unit.trim() === '') {
    return defaultUnit;
  }
  return unit;
};

/**
 * Main calibration section component that displays calibration data for all hormones
 * Features error margin control with debouncing and accordion state preservation
 * 
 * @param lotId - The ID of the lot to display calibration data for
 * @param lhUnit - Optional unit for LH hormone (defaults to 'mIU/mL')
 * @param pgUnit - Optional unit for PG hormone (defaults to 'ng/mL')
 * @param lrE3gUnit - Optional unit for LR E3G hormone (defaults to 'ng/mL')
 */
const OvLotCalibrationSection: FC<OvLotCalibrationSectionProps> = ({
  lotId,
  lhUnit,
  pgUnit,
  lrE3gUnit,
}) => {
  const { t } = useTranslation();
  
  // Use default units if not provided or invalid
  const safeLhUnit = getSafeUnit(lhUnit, DEFAULT_LH_UNIT);
  const safePgUnit = getSafeUnit(pgUnit, DEFAULT_PG_UNIT);
  const safeLrE3gUnit = getSafeUnit(lrE3gUnit, DEFAULT_LR_E3G_UNIT);
  
  const [errorMargin, setErrorMargin] = useState<number>(DEFAULT_ERROR_MARGIN);
  const [debouncedErrorMargin, setDebouncedErrorMargin] = useState<number>(DEFAULT_ERROR_MARGIN);
  
  // Track accordion expanded state to preserve it during loading
  const [expandedAccordions, setExpandedAccordions] = useState<{
    lh: boolean;
    pg: boolean;
    lr_e3g: boolean;
  }>({
    lh: true,
    pg: true,
    lr_e3g: true,
  });

  // Debounce error margin changes to avoid too frequent API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedErrorMargin(errorMargin);
    }, ERROR_MARGIN_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [errorMargin]);

  const { data, isLoading, error, isFetching } = useLotCalibrationData({
    lotId,
    errorMargin: debouncedErrorMargin,
    enabled: !!lotId,
  });

  // Check if there's any relevant calibration data (truthTable with entries)
  // Must be called before any early returns (React Hooks rules)
  const hasRelevantData = useMemo(() => {
    const calibrationData = data?.calibrationData;
    if (!calibrationData) return false;
    return Object.values(calibrationData).some(
      (hormoneData) =>
        hormoneData &&
        hormoneData.truthTable &&
        hormoneData.truthTable.length > 0
    );
  }, [data?.calibrationData]);

  // Check if any hormone has error margin data (for showing the slider)
  // Must be called before any early returns (React Hooks rules)
  const hasErrorMarginData = useMemo(() => {
    const calibrationData = data?.calibrationData;
    if (!calibrationData) return false;
    return Object.values(calibrationData).some(
      (hormoneData) =>
        hormoneData &&
        hormoneData.truthTable &&
        hormoneData.truthTable.length > 0 &&
        hormoneData.errorMargin !== undefined &&
        hormoneData.errorMargin > 0
    );
  }, [data?.calibrationData]);

  // Show loading only on initial load, not when refetching
  if (isLoading && !data) {
    return (
      <Container>
        <OvLoadingIndicator />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">
          {t('lots.calibration.errorLoading')}
        </Alert>
      </Container>
    );
  }

  if (!data?.calibrationData) {
    return (
      <Container>
        <Alert severity="info">
          {t('lots.calibration.noCalibrationData')}
        </Alert>
      </Container>
    );
  }

  const { calibrationData } = data;

  // If no relevant data, show informational message
  if (!hasRelevantData) {
    return (
      <Container>
        <Alert severity="info">
          {t('lots.calibration.noRelevantData')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      {/* Only show error margin slider if there's error margin data */}
      {hasErrorMarginData && (
        <ErrorMarginWrapper>
          <OvErrorMarginControl
            value={errorMargin}
            onChange={setErrorMargin}
            min={1}
            max={20}
          />
        </ErrorMarginWrapper>
      )}

      <ContentWrapper>
        {/* Show subtle loading indicator when refetching */}
        {isFetching && data && (
          <LoadingOverlay>
            <OvLoadingIndicator />
          </LoadingOverlay>
        )}

      {calibrationData.lh && (
        <StyledAccordion 
          expanded={expandedAccordions.lh}
          onChange={(_, expanded) => 
            setExpandedAccordions((prev) => ({ ...prev, lh: expanded }))
          }
        >
          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AccordionTitle>{t('common.luteinizing')}</AccordionTitle>
          </StyledAccordionSummary>
          <AccordionDetails>
            <OvHormoneCalibrationCard
              hormone="lh"
              data={calibrationData.lh}
              unit={safeLhUnit}
            />
          </AccordionDetails>
        </StyledAccordion>
      )}

      {calibrationData.pg && (
        <StyledAccordion 
          expanded={expandedAccordions.pg}
          onChange={(_, expanded) => 
            setExpandedAccordions((prev) => ({ ...prev, pg: expanded }))
          }
        >
          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AccordionTitle>{t('common.progesterone')}</AccordionTitle>
          </StyledAccordionSummary>
          <AccordionDetails>
            <OvHormoneCalibrationCard
              hormone="pg"
              data={calibrationData.pg}
              unit={safePgUnit}
            />
          </AccordionDetails>
        </StyledAccordion>
      )}

      {calibrationData.lr_e3g && (
        <StyledAccordion 
          expanded={expandedAccordions.lr_e3g}
          onChange={(_, expanded) => 
            setExpandedAccordions((prev) => ({ ...prev, lr_e3g: expanded }))
          }
        >
          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AccordionTitle>{t('common.lowRangeEstrogen')}</AccordionTitle>
          </StyledAccordionSummary>
          <AccordionDetails>
            <OvHormoneCalibrationCard
              hormone="lr_e3g"
              data={calibrationData.lr_e3g}
              unit={safeLrE3gUnit}
            />
          </AccordionDetails>
        </StyledAccordion>
      )}
      </ContentWrapper>
    </Container>
  );
};

export default OvLotCalibrationSection;

const Container = styled.div`
  margin-top: 1.5rem;
`;

const ErrorMarginWrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const StyledAccordion = styled(Accordion)`
  && {
    box-shadow: none;
    border: 1px solid ${Colours.GRAY['200']};
    border-radius: ${Variables.borderRadius.SMALL};
    margin-bottom: 1rem;

    &:before {
      display: none;
    }
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    background-color: ${Colours.OV_LIGHT_GRAY};
    border-radius: ${Variables.borderRadius.SMALL};

    &.Mui-expanded {
      border-radius: ${Variables.borderRadius.SMALL} ${Variables.borderRadius.SMALL} 0 0;
    }
  }
`;

const AccordionTitle = styled.span`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  color: ${Colours.OV_BASE};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: ${Variables.borderRadius.SMALL};
  z-index: 10;
  pointer-events: none;
`;

